import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { Check, Minus } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  checkboxIndicatorRecipe,
  checkboxRecipe,
  controlRowRecipe,
  switchRecipe,
  switchThumbRecipe,
  type CheckboxRecipeProps,
} from '../recipes/control.recipe';
import { fieldDescriptionRecipe } from '../recipes/field.recipe';
import { Label } from './Field';
import { useFieldControlProps } from './field-context';

type CheckboxSize = NonNullable<CheckboxRecipeProps['size']>;
/** A switch has no phone-sized `lg`; it is already a thumb's width. */
type SwitchSize = Exclude<CheckboxSize, 'lg'>;

export interface CheckboxProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    'children'
  > {
  size?: CheckboxSize;
  /** Text beside the box. Clicking it toggles, because Label wires htmlFor. */
  label?: React.ReactNode;
  /** Helper line under the label. */
  description?: React.ReactNode;
}

/**
 * A checkbox, for a value that is submitted with a form.
 *
 * Supports the indeterminate state via `checked="indeterminate"` — needed for
 * "select all" headers where only some rows are ticked.
 *
 *   <Checkbox label="Vehicle released" checked={v} onCheckedChange={setV} />
 */
export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(function Checkbox(
  { className, size = 'sm', label, description, id, disabled, ...props },
  ref
) {
  const generated = React.useId();
  const field = useFieldControlProps({ id, disabled });
  const controlId = field.id ?? `${generated}-checkbox`;
  const descriptionId = description ? `${generated}-description` : undefined;

  const box = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={controlId}
      disabled={field.disabled}
      aria-describedby={
        [descriptionId, field['aria-describedby']].filter(Boolean).join(' ') ||
        undefined
      }
      aria-invalid={field['aria-invalid']}
      className={cn(checkboxRecipe({ size }), className)}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={checkboxIndicatorRecipe({ size })}
      >
        {props.checked === 'indeterminate' ? (
          <Minus strokeWidth={3} />
        ) : (
          <Check strokeWidth={3} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  if (!label && !description) return box;

  return (
    <div className={controlRowRecipe()}>
      {box}
      <div className="relay-ui-internal grid gap-0.5 leading-none">
        {label ? <Label htmlFor={controlId}>{label}</Label> : null}
        {description ? (
          <p id={descriptionId} className={fieldDescriptionRecipe()}>
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
});

export interface SwitchProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
    'children'
  > {
  size?: SwitchSize;
  label?: React.ReactNode;
  description?: React.ReactNode;
  /**
   * Put the switch after the label, pushed to the far edge — the settings-row
   * layout. On by default when there is a label, since that is nearly always
   * how a switch is used.
   */
  reverse?: boolean;
}

/**
 * A switch, for a setting that takes effect immediately.
 *
 * If the change only applies once the user presses Save, it should be a
 * Checkbox instead — a switch that does not switch anything is a lie.
 */
export const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(function Switch(
  {
    className,
    size = 'sm',
    label,
    description,
    reverse = true,
    id,
    disabled,
    ...props
  },
  ref
) {
  const generated = React.useId();
  const field = useFieldControlProps({ id, disabled });
  const controlId = field.id ?? `${generated}-switch`;
  const descriptionId = description ? `${generated}-description` : undefined;

  const control = (
    <SwitchPrimitive.Root
      ref={ref}
      id={controlId}
      disabled={field.disabled}
      aria-describedby={
        [descriptionId, field['aria-describedby']].filter(Boolean).join(' ') ||
        undefined
      }
      className={cn(switchRecipe({ size }), className)}
      {...props}
    >
      <SwitchPrimitive.Thumb className={switchThumbRecipe({ size })} />
    </SwitchPrimitive.Root>
  );

  if (!label && !description) return control;

  return (
    <div className={controlRowRecipe({ reverse })}>
      {control}
      <div className="relay-ui-internal grid gap-0.5 leading-none">
        {label ? <Label htmlFor={controlId}>{label}</Label> : null}
        {description ? (
          <p id={descriptionId} className={fieldDescriptionRecipe()}>
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
});
